import { ConsultationType } from '../../@types'
import { Product } from '../../data/use-products/types'
import { EventNames } from '../types'

enum ItemVariant {
  OnDemand = 'OnDemand',
  Scheduled = 'Scheduled',
}

export enum ReferralSource {
  CHATBOT_RECOMMENDATION = 'ChatbotRecommendation',
  NONE = 'None',
}

interface GTMEvent {
  toObject(): Record<string, unknown>
}

export class GoogleTagManager {
  static pushEvent(event: GTMEvent) {
    if (!window) {
      console.warn('Failed to push event to GTM: window is not defined.')
      return false
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(event.toObject())

    return true
  }
}

// SIGN_UP
export class GTMEventSignUp implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.SIGN_UP}`,
    }
  }

  static new() {
    return new GTMEventSignUp()
  }
}

// PURCHASE
export class GTMEventPurchase implements GTMEvent {
  private readonly transactionId: string
  private readonly product: Product
  private readonly variant: ItemVariant
  private readonly discount: number
  private readonly coupon: string

  constructor(
    transactionId: string,
    product: Product,
    consultationType: ConsultationType,
    discount: number,
    coupon: string
  ) {
    this.transactionId = transactionId
    this.product = product
    this.variant = consultationType === ConsultationType.ON_DEMAND ? ItemVariant.OnDemand : ItemVariant.Scheduled
    this.discount = discount
    this.coupon = coupon
  }

  public toObject() {
    return {
      event: `test_${EventNames.PURCHASE}`,
      transaction_id: this.transactionId,
      item_id: this.product.id,
      item_category: this.product.category.id,
      item_variant: this.variant,
      quantity: 1,
      price: this.product.price,
      discount: this.discount,
      coupon: this.coupon,
    }
  }

  static new(
    transactionId: string,
    product: Product,
    consultationType: ConsultationType,
    discount: number,
    coupon: string
  ) {
    return new GTMEventPurchase(transactionId, product, consultationType, discount, coupon)
  }
}

// ADD_PAYMENT_INFO
export class GTMEventAddPaymentInfo implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.ADD_PAYMENT_INFO}`,
    }
  }

  static new() {
    return new GTMEventAddPaymentInfo()
  }
}

// BEGIN_CHECKOUT
export class GTMEventBeginCheckout implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.BEGIN_CHECKOUT}`,
    }
  }

  static new() {
    return new GTMEventBeginCheckout()
  }
}

// SCHEDULE_ITEM
export class GTMEventTrackScheduleItem implements GTMEvent {
  private readonly referralSource: ReferralSource

  constructor(referralSource: ReferralSource) {
    this.referralSource = referralSource
  }

  public toObject() {
    return {
      event: `test_${EventNames.SCHEDULE_ITEM}`,
      referral_source: this.referralSource,
    }
  }

  static new(referralSource: ReferralSource) {
    return new GTMEventTrackScheduleItem(referralSource)
  }
}

// TRACK_PAY_METHOD
export class GTMEventTrackPayMethod implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.TRACK_PAY_METHOD}`,
    }
  }

  static new() {
    return new GTMEventTrackPayMethod()
  }
}

// SELECT_ITEM
export class GTMEventSelectItem implements GTMEvent {
  private readonly product: Product
  private readonly variant: ItemVariant
  private readonly referralSource: ReferralSource

  constructor(product: Product, consultationType: ConsultationType, referralSource: ReferralSource) {
    this.product = product
    this.variant = consultationType === ConsultationType.ON_DEMAND ? ItemVariant.OnDemand : ItemVariant.Scheduled
    this.referralSource = referralSource
  }

  public toObject() {
    return {
      event: `test_${EventNames.SELECT_ITEM}`,
      item_id: this.product.id,
      item_name: this.product.name,
      item_category: this.product.category.id,
      item_variant: this.variant,
      referral_source: this.referralSource,
    }
  }

  static new(product: Product, consultationType: ConsultationType, referralSource: ReferralSource) {
    return new GTMEventSelectItem(product, consultationType, referralSource)
  }
}

// TRACK_SIGN
export class GTMEventTrackSign implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.TRACK_SIGN}`,
    }
  }

  static new() {
    return new GTMEventTrackSign()
  }
}

// TUTORIAL_COMPLETE
export class GTMEventTutorialComplete implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.TUTORIAL_COMPLETE}`,
    }
  }

  static new() {
    return new GTMEventTutorialComplete()
  }
}

// TRACK_WARNING
export class GTMEventTrackWarning implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.TRACK_WARNING}`,
    }
  }

  static new() {
    return new GTMEventTrackWarning()
  }
}

// TRACK_CONFIRM_AGE
export class GTMEventTrackConfirmAge implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.TRACK_CONFIRM_AGE}`,
    }
  }

  static new() {
    return new GTMEventTrackConfirmAge()
  }
}

// TUTORIAL_BEGIN
export class GTMEventTutorialBegin implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.TUTORIAL_BEGIN}`,
    }
  }

  static new() {
    return new GTMEventTutorialBegin()
  }
}

// PAGE_VIEW
export class GTMEventPageView implements GTMEvent {
  public toObject() {
    return {
      event: `test_${EventNames.PAGE_VIEW}`,
    }
  }

  static new() {
    return new GTMEventPageView()
  }
}
